import { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import AdminNotificationsService from "../services/adminNotifications.service";
import { showAdminAlert, ensureNotificationPermission } from "../utils/showBrowserNotification";

const getSocketUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }
  const apiUrl = process.env.REACT_APP_APIURL || "";
  return apiUrl.replace(/\/api\/?$/, "");
};

const formatTime = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "";
  }
};

const getAdminUserId = () => {
  try {
    const storedUser = JSON.parse(localStorage.getItem("user") || "null");
    return storedUser?._id ? String(storedUser._id) : null;
  } catch {
    return null;
  }
};

export default function useAdminNotifications(adminUserId) {
  const resolvedAdminUserId = adminUserId ? String(adminUserId) : getAdminUserId();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const socketRef = useRef(null);

  const refreshUnreadCount = useCallback(async () => {
    if (!resolvedAdminUserId) return;
    try {
      const response = await AdminNotificationsService.getUnreadCount();
      if (response.data?.status) {
        setUnreadCount(response.data.data?.unread_count || 0);
      }
    } catch (error) {
      console.error("Failed to fetch unread admin notifications:", error);
    }
  }, [resolvedAdminUserId]);

  const loadNotifications = useCallback(async () => {
    if (!resolvedAdminUserId) return;
    setLoading(true);
    try {
      const response = await AdminNotificationsService.getAdminNotifications(20, 0);
      if (response.data?.status) {
        setNotifications(response.data.data || []);
        setUnreadCount(response.data.count?.unread_count || 0);
      }
    } catch (error) {
      console.error("Failed to load admin notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [resolvedAdminUserId]);

  const markNotificationRead = useCallback(async (notification) => {
    if (!notification?._id) return;
    try {
      await AdminNotificationsService.markAsRead(notification._id);
      setNotifications((prev) =>
        prev.map((item) =>
          item._id === notification._id
            ? { ...item, is_read_for_admin: true }
            : item
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await AdminNotificationsService.markAllAsRead();
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, is_read_for_admin: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }, []);

  useEffect(() => {
    if (!resolvedAdminUserId) return;

    loadNotifications();

    ensureNotificationPermission();

    const socket = io(getSocketUrl(), {
      transports: ["websocket", "polling"],
      withCredentials: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("join-role-room", { role: "admin", userId: resolvedAdminUserId });
    });

    socket.on("connect_error", (error) => {
      console.error("Admin notification socket connection error:", error.message);
    });

    socket.on("admin-notification", (payload) => {
      setNotifications((prev) => {
        const exists = prev.some((item) => String(item._id) === String(payload._id));
        if (exists) return prev;
        return [{ ...payload, is_read_for_admin: false }, ...prev].slice(0, 20);
      });
      setUnreadCount((prev) => prev + 1);
      showAdminAlert(payload);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [resolvedAdminUserId, loadNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    refreshUnreadCount,
    markNotificationRead,
    markAllRead,
    formatTime,
  };
}
